import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";

import {
  useCloudStreamsQuery,
  useDeleteCloudStream,
} from "../hooks/useCloudStreams";
import type { CloudStreamStatus } from "../types";

const STATUS_BADGE: Record<
  CloudStreamStatus,
  { label: string; className: string }
> = {
  creating: { label: "Creando", className: "bg-amber-500/20 text-amber-400" },
  active: { label: "Activo", className: "bg-green-500/20 text-green-400" },
  paused: { label: "Pausado", className: "bg-blue-500/20 text-blue-400" },
  stopped: {
    label: "Detenido",
    className: "bg-neutral-500/20 text-neutral-400",
  },
  error: { label: "Error", className: "bg-red-500/20 text-red-400" },
  archived: {
    label: "Archivado",
    className: "bg-neutral-700/40 text-neutral-400",
  },
};

const mask = (url: string | null): string => {
  if (!url) return "—";
  return url.length > 42 ? `${url.slice(0, 42)}…` : url;
};

const CloudStreamsPage = () => {
  const { data, isLoading, isFetching } = useCloudStreamsQuery();
  const deleteMutation = useDeleteCloudStream();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const streams = data?.data ?? [];
  const isBusy = isLoading || isFetching || deleteMutation.isPending;

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      setDeleteConfirmId(null);
    } catch {
      // Silent — surface via mutation.error in UI. Never log payloads.
    }
  };

  const statusBadge = (status: CloudStreamStatus) => {
    const s = STATUS_BADGE[status];
    return (
      <Badge className={`${s.className} hover:opacity-90`}>{s.label}</Badge>
    );
  };

  return (
    <div className="relative flex flex-col gap-6">
      <PageHeader
        title="Cloud Streams"
        subtitle="Streams gestionados por Cloudflare (transcode / restream / VOD)"
      />

      <Card className="border-neutral-800 bg-neutral-900/50">
        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead className="text-neutral-400">Nombre</TableHead>
              <TableHead className="text-neutral-400">User</TableHead>
              <TableHead className="text-neutral-400">Ingest URL</TableHead>
              <TableHead className="text-neutral-400">Destinos</TableHead>
              <TableHead className="text-neutral-400">Grabación</TableHead>
              <TableHead className="text-neutral-400">Estado</TableHead>
              <TableHead className="text-neutral-400">Creado</TableHead>
              <TableHead className="text-right text-neutral-400">
                Acciones
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {streams.map((stream) => (
              <TableRow key={stream.id} className="border-neutral-800">
                <TableCell className="font-medium text-neutral-100">
                  {stream.name}
                </TableCell>
                <TableCell className="text-neutral-400">
                  {stream.userId ?? "—"}
                </TableCell>
                <TableCell className="font-mono text-xs text-neutral-400">
                  {mask(stream.ingestUrl)}
                </TableCell>
                <TableCell className="text-neutral-300">
                  {stream.restreamLinks?.length ?? 0}
                </TableCell>
                <TableCell>
                  {stream.autoRecordEnabled ? (
                    <Badge className="bg-green-500/20 text-green-400">On</Badge>
                  ) : (
                    <Badge className="bg-neutral-500/20 text-neutral-400">
                      Off
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{statusBadge(stream.status)}</TableCell>
                <TableCell className="text-neutral-400">
                  {formatDistanceToNow(new Date(stream.createdAt), {
                    addSuffix: true,
                    locale: es,
                  })}
                </TableCell>
                <TableCell className="text-right">
                  {deleteConfirmId === stream.id ? (
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(stream.id)}
                      >
                        Confirmar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300"
                      onClick={() => setDeleteConfirmId(stream.id)}
                    >
                      Eliminar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!isLoading && streams.length === 0 && (
          <EmptyState
            title="Sin streams Cloudflare"
            description="Todavía no se creó ningún stream gestionado por Cloudflare."
          />
        )}
      </Card>

      {isBusy && <LoadingOverlay />}
    </div>
  );
};

export default CloudStreamsPage;
